import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { complete } from '@/lib/ai'
import {
  Brain, Sparkles, MessageSquare, FileText, Users, TrendingUp,
  Mail, Send, X, Copy, Check, ChevronDown, Loader2, Zap,
  RefreshCw, ThumbsUp, ThumbsDown,
} from 'lucide-react'
import { toast } from 'sonner'

// ─── Feature Card ─────────────────────────────────────────────────────────────
function FeatureCard({ icon: Icon, title, description, color, onClick }: any) {
  return (
    <motion.div whileHover={{ y: -3, scale: 1.01 }} onClick={onClick}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '20px 22px', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'border-color 150ms ease' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = `color-mix(in srgb, ${color} var(--status-tint-edge), transparent)`}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: `radial-gradient(circle, color-mix(in srgb, ${color} var(--status-tint-fill), transparent) 0%, transparent 70%)` }} />
      <div style={{ width: 40, height: 40, borderRadius: 12, background: `color-mix(in srgb, ${color} var(--status-tint-dim), transparent)`, border: `1px solid color-mix(in srgb, ${color} var(--status-tint-strong), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <Icon style={{ width: 18, height: 18, color }} />
      </div>
      <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 6px' }}>{title}</p>
      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.5 }}>{description}</p>
    </motion.div>
  )
}

// ─── Result Box ───────────────────────────────────────────────────────────────
function ResultBox({ result, onCopy, onRegenerate, loading }: any) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(result)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    onCopy?.()
  }

  if (!result && !loading) return null

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      style={{ background: 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', borderRadius: 14, padding: '16px 18px', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Sparkles style={{ width: 14, height: 14, color: 'var(--accent-text)' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-text)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>AI Result</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onRegenerate} title="Regenerate"
            style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RefreshCw style={{ width: 12, height: 12 }} />
          </button>
          <button onClick={handleCopy} title="Copy"
            style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: copied ? 'var(--positive)' : 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {copied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
          </button>
        </div>
      </div>
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <Loader2 style={{ width: 16, height: 16, color: 'var(--accent-text)', animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>Generating...</span>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--ink-primary)', margin: 0, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{result}</p>
      )}
    </motion.div>
  )
}

// ─── Suggested Replies Panel ──────────────────────────────────────────────────
function SuggestedReplies({ conversations }: any) {
  const [selectedConv, setSelectedConv] = useState<any>(null)
  const [tone, setTone] = useState('professional')
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)

  const generate = async () => {
    if (!selectedConv) { toast.error('Select a conversation first'); return }
    setLoading(true); setResult('')
    const prompt = `Generate a ${tone} reply to this customer conversation.

Subject: ${selectedConv.subject || 'Support request'}
Customer: ${selectedConv.contact_name || 'Customer'}
Last message: ${selectedConv.last_message || 'No message content'}
Status: ${selectedConv.status}
Priority: ${selectedConv.priority}

Write a helpful, ${tone} reply that addresses their concern. Keep it concise (2-3 paragraphs max).`
    const { data, error } = await complete({ prompt })
    if (error) {
      setResult(`Error: ${error.message}`)
    } else {
      setResult(data!.text)
    }
    setLoading(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select conversation</label>
        <select onChange={e => {
          const conv = conversations.find((c: any) => c.id === e.target.value)
          setSelectedConv(conv || null)
          setResult('')
        }}
          style={{ width: '100%', padding: '9px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
          <option value="">Choose a conversation...</option>
          {conversations.map((c: any) => (
            <option key={c.id} value={c.id}>{c.contact_name || 'Unknown'} — {c.subject || c.last_message?.slice(0, 40) || 'No subject'}</option>
          ))}
        </select>
      </div>

      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tone</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {['professional', 'friendly', 'empathetic', 'direct'].map(t => (
            <button key={t} onClick={() => setTone(t)}
              style={{ flex: 1, padding: '7px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: tone === t ? 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)' : 'var(--hover-bg)', color: tone === t ? 'var(--accent-text)' : 'var(--ink-tertiary)', border: tone === t ? '1px solid color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : '1px solid var(--border-subtle)' }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <button onClick={generate} disabled={loading || !selectedConv}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: loading || !selectedConv ? 'not-allowed' : 'pointer', opacity: !selectedConv ? 0.5 : 1, boxShadow: 'var(--logo-glow)' }}>
        {loading ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : <Sparkles style={{ width: 15, height: 15 }} />}
        Generate reply
      </button>

      <ResultBox result={result} loading={loading} onRegenerate={generate} onCopy={() => toast.success('Copied to clipboard')} />
    </div>
  )
}

// ─── Conversation Summary Panel ───────────────────────────────────────────────
function ConversationSummary({ conversations }: any) {
  const [selectedConv, setSelectedConv] = useState<any>(null)
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)

  const generate = async () => {
    if (!selectedConv) { toast.error('Select a conversation first'); return }
    setLoading(true); setResult('')
    const prompt = `Summarize this customer conversation for a support agent.

Subject: ${selectedConv.subject || 'Support request'}
Customer: ${selectedConv.contact_name || 'Customer'}
Channel: ${selectedConv.channel}
Status: ${selectedConv.status}
Priority: ${selectedConv.priority}
Last message: ${selectedConv.last_message || 'No message content'}
Created: ${selectedConv.created_at}

Provide:
1. A 1-2 sentence summary of the issue
2. Current status and what's been done
3. Recommended next action`
    const { data, error } = await complete({ prompt })
    if (error) {
      setResult(`Error: ${error.message}`)
    } else {
      setResult(data!.text)
    }
    setLoading(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select conversation</label>
        <select onChange={e => {
          const conv = conversations.find((c: any) => c.id === e.target.value)
          setSelectedConv(conv || null)
          setResult('')
        }}
          style={{ width: '100%', padding: '9px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
          <option value="">Choose a conversation...</option>
          {conversations.map((c: any) => (
            <option key={c.id} value={c.id}>{c.contact_name || 'Unknown'} — {c.subject || c.last_message?.slice(0, 40) || 'No subject'}</option>
          ))}
        </select>
      </div>

      <button onClick={generate} disabled={loading || !selectedConv}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: loading || !selectedConv ? 'not-allowed' : 'pointer', opacity: !selectedConv ? 0.5 : 1, boxShadow: 'var(--logo-glow)' }}>
        {loading ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : <Sparkles style={{ width: 15, height: 15 }} />}
        Summarize conversation
      </button>

      <ResultBox result={result} loading={loading} onRegenerate={generate} onCopy={() => toast.success('Copied to clipboard')} />
    </div>
  )
}

// ─── Contact Categorizer Panel ────────────────────────────────────────────────
function ContactCategorizer({ contacts }: any) {
  const [selectedContact, setSelectedContact] = useState<any>(null)
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)

  const generate = async () => {
    if (!selectedContact) { toast.error('Select a contact first'); return }
    setLoading(true); setResult('')
    const prompt = `Analyze this customer contact and provide categorization suggestions.

Name: ${selectedContact.first_name} ${selectedContact.last_name || ''}
Email: ${selectedContact.email || 'N/A'}
Company: ${selectedContact.company_name || 'N/A'}
Current status: ${selectedContact.status}
Current tags: ${selectedContact.tags?.join(', ') || 'None'}
Notes: ${selectedContact.notes || 'None'}

Provide:
1. Suggested status (active/lead/inactive) with reasoning
2. 3-5 relevant tags to add
3. Contact segment they likely belong to
4. Recommended next action to take with this contact`
    const { data, error } = await complete({ prompt })
    if (error) {
      setResult(`Error: ${error.message}`)
    } else {
      setResult(data!.text)
    }
    setLoading(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select contact</label>
        <select onChange={e => {
          const contact = contacts.find((c: any) => c.id === e.target.value)
          setSelectedContact(contact || null)
          setResult('')
        }}
          style={{ width: '100%', padding: '9px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
          <option value="">Choose a contact...</option>
          {contacts.map((c: any) => (
            <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.company_name ? `— ${c.company_name}` : ''}</option>
          ))}
        </select>
      </div>

      <button onClick={generate} disabled={loading || !selectedContact}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: loading || !selectedContact ? 'not-allowed' : 'pointer', opacity: !selectedContact ? 0.5 : 1, boxShadow: 'var(--logo-glow)' }}>
        {loading ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : <Sparkles style={{ width: 15, height: 15 }} />}
        Analyze contact
      </button>

      <ResultBox result={result} loading={loading} onRegenerate={generate} onCopy={() => toast.success('Copied to clipboard')} />
    </div>
  )
}

// ─── Deal Insights Panel ──────────────────────────────────────────────────────
function DealInsights({ deals }: any) {
  const [selectedDeal, setSelectedDeal] = useState<any>(null)
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)

  const generate = async () => {
    if (!selectedDeal) { toast.error('Select a deal first'); return }
    setLoading(true); setResult('')
    const prompt = `Analyze this sales deal and provide strategic insights.

Deal: ${selectedDeal.title}
Contact: ${selectedDeal.contact_name || 'N/A'}
Company: ${selectedDeal.company_id || 'N/A'}
Stage: ${selectedDeal.stage_name || 'Unknown'}
Value: $${selectedDeal.value || 0}
Probability: ${selectedDeal.probability || 0}%
Status: ${selectedDeal.status}
Close date: ${selectedDeal.close_date || 'Not set'}
Notes: ${selectedDeal.notes || 'None'}

Provide:
1. Win probability assessment and key factors
2. Main risks that could cause this deal to be lost
3. 3 specific next steps to advance this deal
4. Suggested talking points for the next conversation`
    const { data, error } = await complete({ prompt })
    if (error) {
      setResult(`Error: ${error.message}`)
    } else {
      setResult(data!.text)
    }
    setLoading(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select deal</label>
        <select onChange={e => {
          const deal = deals.find((d: any) => d.id === e.target.value)
          setSelectedDeal(deal || null)
          setResult('')
        }}
          style={{ width: '100%', padding: '9px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
          <option value="">Choose a deal...</option>
          {deals.map((d: any) => (
            <option key={d.id} value={d.id}>{d.title} — {d.stage_name || 'No stage'} {d.value ? `($${d.value.toLocaleString()})` : ''}</option>
          ))}
        </select>
      </div>

      <button onClick={generate} disabled={loading || !selectedDeal}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: loading || !selectedDeal ? 'not-allowed' : 'pointer', opacity: !selectedDeal ? 0.5 : 1, boxShadow: 'var(--logo-glow)' }}>
        {loading ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : <Sparkles style={{ width: 15, height: 15 }} />}
        Get deal insights
      </button>

      <ResultBox result={result} loading={loading} onRegenerate={generate} onCopy={() => toast.success('Copied to clipboard')} />
    </div>
  )
}

// ─── Email Draft Generator Panel ─────────────────────────────────────────────
function EmailDraftGenerator({ contacts }: any) {
  const [form, setForm] = useState({
    recipient: '',
    purpose: 'follow-up',
    context: '',
    tone: 'professional',
  })
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  const generate = async () => {
    if (!form.context.trim()) { toast.error('Please provide some context'); return }
    setLoading(true); setResult('')
    const selectedContact = contacts.find((c: any) => c.id === form.recipient)
    const prompt = `Write a ${form.tone} ${form.purpose} email.

${selectedContact ? `Recipient: ${selectedContact.first_name} ${selectedContact.last_name || ''} at ${selectedContact.company_name || 'their company'}` : 'Recipient: A customer'}
Purpose: ${form.purpose}
Context/Details: ${form.context}
Tone: ${form.tone}

Write a complete email with subject line and body. Format as:
Subject: [subject line]

[email body]`
    const { data, error } = await complete({ prompt })
    if (error) {
      setResult(`Error: ${error.message}`)
    } else {
      setResult(data!.text)
    }
    setLoading(false)
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '9px 12px',
    background: 'var(--hover-bg)',
    border: '1px solid var(--border-default)',
    borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)',
    outline: 'none', boxSizing: 'border-box',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Recipient (optional)</label>
        <select value={form.recipient} onChange={e => set('recipient', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          <option value="">Generic recipient</option>
          {contacts.map((c: any) => (
            <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.company_name ? `— ${c.company_name}` : ''}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Purpose</label>
          <select value={form.purpose} onChange={e => set('purpose', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
            <option value="follow-up">Follow-up</option>
            <option value="introduction">Introduction</option>
            <option value="proposal">Proposal</option>
            <option value="check-in">Check-in</option>
            <option value="thank you">Thank you</option>
            <option value="re-engagement">Re-engagement</option>
            <option value="invoice reminder">Invoice reminder</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tone</label>
          <select value={form.tone} onChange={e => set('tone', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="formal">Formal</option>
            <option value="casual">Casual</option>
          </select>
        </div>
      </div>

      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Context / Key points *</label>
        <textarea value={form.context} onChange={e => set('context', e.target.value)}
          placeholder="e.g. Following up on our meeting last Tuesday, they were interested in the enterprise plan, mentioned budget concerns..."
          rows={3}
          style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
          onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
          onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
      </div>

      <button onClick={generate} disabled={loading || !form.context.trim()}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: loading || !form.context.trim() ? 'not-allowed' : 'pointer', opacity: !form.context.trim() ? 0.5 : 1, boxShadow: 'var(--logo-glow)' }}>
        {loading ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : <Mail style={{ width: 15, height: 15 }} />}
        Draft email
      </button>

      <ResultBox result={result} loading={loading} onRegenerate={generate} onCopy={() => toast.success('Copied to clipboard')} />
    </div>
  )
}

// ─── Free Chat Panel ──────────────────────────────────────────────────────────
function FreeChat() {
  const { workspace } = useAuth()
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const send = async () => {
    if (!input.trim() || loading) return
    const userMsg = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: userMsg }])
    setLoading(true)

    const { data, error } = await complete({
      messages: [...messages, { role: 'user', content: userMsg }],
      system: `You are an AI assistant for ${workspace?.name || 'a business'} using Orkestra, a customer operations platform. Help with customer service, sales, marketing, and business operations. Be concise and actionable.`,
    })
    if (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${error.message}` }])
      toast.error('Failed to get AI response')
    } else {
      setMessages(prev => [...prev, { role: 'assistant', content: data!.text }])
    }
    setLoading(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400 }}>
      {/* Messages */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 8 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <Brain style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 10px', display: 'block' }} />
            <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: '0 0 6px', fontWeight: 600 }}>Ask me anything</p>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>I can help with customer service, sales strategy, email drafts, and more</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {[
                'How do I handle a difficult customer?',
                'Write a follow-up email template',
                'Tips for improving close rates',
                'How to upsell existing customers',
              ].map(suggestion => (
                <button key={suggestion} onClick={() => setInput(suggestion)}
                  style={{ padding: '6px 12px', background: 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', borderRadius: 20, fontSize: 12, color: 'var(--accent-text)', cursor: 'pointer' }}>
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            {msg.role === 'assistant' && (
              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0, alignSelf: 'flex-start', marginTop: 2 }}>
                <Sparkles style={{ width: 13, height: 13, color: 'var(--accent-ink)' }} />
              </div>
            )}
            <div style={{
              maxWidth: '80%', padding: '10px 14px', borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: msg.role === 'user' ? 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)' : 'var(--hover-bg)',
              border: `1px solid ${msg.role === 'user' ? 'color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : 'var(--border-subtle)'}`,
              fontSize: 13, color: msg.role === 'user' ? 'var(--accent-text)' : 'var(--ink-primary)',
              lineHeight: 1.6, whiteSpace: 'pre-wrap',
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Sparkles style={{ width: 13, height: 13, color: 'var(--accent-ink)' }} />
            </div>
            <div style={{ padding: '10px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: '14px 14px 14px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Loader2 style={{ width: 13, height: 13, color: 'var(--accent-text)', animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Thinking...</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          placeholder="Ask anything..."
          style={{ flex: 1, padding: '10px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 10, fontSize: 13, color: 'var(--ink-primary)', outline: 'none' }}
          onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
          onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
        <button onClick={send} disabled={loading || !input.trim()}
          style={{ width: 40, height: 40, borderRadius: 10, background: input.trim() ? 'var(--accent)' : 'var(--hover-bg)', border: 'none', cursor: input.trim() ? 'pointer' : 'not-allowed', color: 'var(--accent-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 150ms ease', flexShrink: 0 }}>
          <Send style={{ width: 15, height: 15 }} />
        </button>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AIHub() {
  const { workspace } = useAuth()
  const [activeFeature, setActiveFeature] = useState<string | null>(null)

  const { data: conversations = [] } = useQuery({
    queryKey: ['ai-conversations', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('conversations')
        .select('id, contact_name, subject, last_message, status, priority, channel, created_at')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false })
        .limit(50)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'ai', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts')
        .select('id, first_name, last_name, email, company_name, status, tags, notes')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false })
        .limit(50)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: deals = [] } = useQuery({
    queryKey: ['ai-deals', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('deals')
        .select('id, title, contact_name, stage_name, value, probability, status, close_date, notes')
        .eq('workspace_id', workspace.id)
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(50)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const FEATURES = [
    {
      id: 'replies',
      icon: MessageSquare,
      title: 'Suggested replies',
      description: 'Generate contextual replies for open conversations with tone control',
      color: 'var(--accent)',
    },
    {
      id: 'summary',
      icon: FileText,
      title: 'Conversation summary',
      description: 'Get a quick summary of any conversation with recommended next steps',
      color: 'var(--info)',
    },
    {
      id: 'contacts',
      icon: Users,
      title: 'Contact categorizer',
      description: 'Analyze contacts and get tag, segment, and status recommendations',
      color: 'var(--positive)',
    },
    {
      id: 'deals',
      icon: TrendingUp,
      title: 'Deal insights',
      description: 'Get win probability, risk factors, and next steps for open deals',
      color: 'var(--warning)',
    },
    {
      id: 'email',
      icon: Mail,
      title: 'Email draft generator',
      description: 'Draft personalized emails for any purpose with one click',
      color: 'var(--danger)',
    },
    {
      id: 'chat',
      icon: Brain,
      title: 'AI assistant',
      description: 'Free-form chat for any business question, strategy, or task',
      color: 'var(--accent-text)',
    },
  ]

  const activeFeatureData = FEATURES.find(f => f.id === activeFeature)

  return (
    <div style={{ height: '100%', overflow: 'auto', background: 'var(--canvas)' }}>
      <div style={{ padding: '28px 32px', maxWidth: 900 }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--logo-glow)' }}>
              <Brain style={{ width: 20, height: 20, color: 'var(--accent-ink)' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>AI Hub</h1>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Powered by Claude</p>
            </div>
          </div>
        </div>

        {/* Feature grid or active panel */}
        <AnimatePresence mode="wait">
          {!activeFeature ? (
            <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                {FEATURES.map(f => (
                  <FeatureCard key={f.id} {...f} onClick={() => setActiveFeature(f.id)} />
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div key="panel" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {/* Panel header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => setActiveFeature(null)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, color: 'var(--ink-tertiary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  ← Back
                </button>
                {activeFeatureData && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 9, background: `color-mix(in srgb, ${activeFeatureData.color} var(--status-tint-dim), transparent)`, border: `1px solid color-mix(in srgb, ${activeFeatureData.color} var(--status-tint-strong), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <activeFeatureData.icon style={{ width: 15, height: 15, color: activeFeatureData.color }} />
                    </div>
                    <div>
                      <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{activeFeatureData.title}</p>
                      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>{activeFeatureData.description}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Panel content */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '22px 24px' }}>
                {activeFeature === 'replies' && <SuggestedReplies conversations={conversations} />}
                {activeFeature === 'summary' && <ConversationSummary conversations={conversations} />}
                {activeFeature === 'contacts' && <ContactCategorizer contacts={contacts} />}
                {activeFeature === 'deals' && <DealInsights deals={deals} />}
                {activeFeature === 'email' && <EmailDraftGenerator contacts={contacts} />}
                {activeFeature === 'chat' && <FreeChat />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}